import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faChartSimple, faLocationDot, faUserCheck } from '@fortawesome/free-solid-svg-icons';

// Shown when an agent opens their own branded hub from the agent platform
// (the "View my hub" promo appends ?campaign=vendorleads). It frames the page
// for the agent: this is what their customers see, it's all tracked in their
// dashboard, and visitors arrive already identified — they only enter an
// address. Anonymous homeowners never see this (no campaign param).

const SEEN_KEY = 'vendorleads_campaign_modal_seen';
const ACCENT = '#10b981';

const POINTS = [
  {
    icon: faChartLine,
    title: 'The real customer experience',
    body: "This is exactly what your customers see when they open your branded insights hub — fully live, not a preview.",
  },
  {
    icon: faChartSimple,
    title: 'Everything is tracked',
    body: 'Every search and action here is captured and shows up in your Vendorleads dashboard, so you can follow each lead.',
  },
  {
    icon: faUserCheck,
    title: 'Visitors are already identified',
    body: "Your customers arrive already known from your link — they never have to tell you who they are.",
  },
  {
    icon: faLocationDot,
    title: 'They just enter their address',
    body: 'That single step is all it takes to get personalised property insights flowing.',
  },
];

export default function VendorleadsCampaignModal() {
  const location = useLocation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    let isCampaign = false;
    try {
      isCampaign = new URLSearchParams(location.search).get('campaign') === 'vendorleads';
    } catch (_) { /* ignore */ }
    if (!isCampaign) return;

    let seen = false;
    try { seen = sessionStorage.getItem(SEEN_KEY) === '1'; } catch (_) { /* ignore */ }
    if (!seen) setShow(true);
  }, [location.search]);

  const handleClose = () => {
    setShow(false);
    try { sessionStorage.setItem(SEEN_KEY, '1'); } catch (_) { /* ignore */ }
  };

  return (
    <Modal show={show} onHide={handleClose} fullscreen="sm-down" centered style={{ zIndex: 99999 }}>
      <Modal.Header closeButton style={{ border: 'none', paddingBottom: 0 }} />
      <Modal.Body style={{ padding: '8px 28px 28px' }}>
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: 16,
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(135deg, ${ACCENT}, #3b82f6)`,
              color: '#fff',
              boxShadow: '0 8px 22px rgba(16,185,129,0.35)',
            }}
          >
            <FontAwesomeIcon icon={faChartLine} style={{ fontSize: 26 }} />
          </div>
          <h2 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em', margin: 0 }}>
            You're viewing your branded hub
          </h2>
          <p style={{ fontSize: '0.92rem', color: '#64748b', lineHeight: 1.55, margin: '8px auto 0', maxWidth: 420 }}>
            Here's what's happening behind the scenes while you look around.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {POINTS.map((p) => (
            <div key={p.title} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', textAlign: 'left' }}>
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `${ACCENT}14`,
                  color: ACCENT,
                }}
              >
                <FontAwesomeIcon icon={p.icon} style={{ fontSize: 16 }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>
                  {p.title}
                </div>
                <div style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.5, marginTop: 2 }}>
                  {p.body}
                </div>
              </div>
            </div>
          ))}
        </div>

        <Button
          onClick={handleClose}
          style={{
            width: '100%',
            background: `linear-gradient(135deg, ${ACCENT}, #3b82f6)`,
            border: 'none',
            color: '#fff',
            borderRadius: 12,
            padding: '14px 0',
            fontWeight: 700,
            fontSize: '0.98rem',
            boxShadow: `0 6px 18px ${ACCENT}40`,
            marginTop: 24,
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.92')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
        >
          Got it — let me explore
        </Button>
      </Modal.Body>
    </Modal>
  );
}
